import { addDays, format } from '@if/core/date'
import {
  EApplicantType,
  EContractStatus,
  EDecision,
  EDocumentType,
  EFinancingProgram,
  EOriginSystemId,
  ERequiredFrom
} from '@if/core/types'
import AddIcon from '@mui/icons-material/Add'
import { Alert, AlertTitle, Box, Chip, Grid, IconButton, Paper, Stack, Typography } from '@mui/material'
import NotFound from '@src/components/NotFound'
import { useUser } from '@src/contexts/AppContext'
import { useUnreadCreditApplicationIds } from '@src/data/api/chat-message/chat-message-api'
import { useCreditApplicationById } from '@src/data/api/credit-api/credit-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { getCanEdit } from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PageSpinner from '../../components/PageSpinner'
import ApplicantInfoBox from './components/applicantInfoBox'
import ContractTile from './components/ContractSection/ContractTile'
import CreditTile from './components/CreditSection/CreditTile'
import DocumentTable from './components/DocumentSection/DocumentTable'
import FundingTile from './components/FundingSection/FundingTile'
import IncomeVerificationTile from './components/IncomeVerificationSection/IncomeVerificationTile'
import LoanInfoBox from './components/LoanInfoBox'
import { MerchantReferenceCodeField } from './components/MerchantReferenceCodeField'
import PrequalTile from './components/PrequalSection/PrequalTile'
import UnreadMessagesModal from './components/UnreadMessagesModal'
import { canPollCreditApp, isServerAnalysingCredit, isServerCreatingContract } from './credit-hooks'

interface Props {
  id: string
  financingProgramId: EFinancingProgram
}
const ViewCreditApplicationPage = ({ id, financingProgramId }: Props) => {
  const { t } = useTranslation()
  const [shouldPoll, setShouldPoll] = useState(false)
  const [showUnreadMessagesModal, setShowUnreadMessagesModal] = useState(false)

  const [creditApplication, isLoadingCreditApp] = useCreditApplicationById(
    {
      creditApplicationId: id,
      financingProgramId,
    },
    shouldPoll,
  )

  const user = useUser()
  const unreadCreditApplicationIds = useUnreadCreditApplicationIds()
  const hasUnread = unreadCreditApplicationIds.has(id)

  const [merchant, isLoadingMerchant] = useMerchantById(creditApplication?.merchantId ?? '')

  const [error, setError] = useState<Error | null>(null)

  const canEdit = getCanEdit(creditApplication?.editLocked)
  const navigate = useNavigate()
  const currentProgramConfig = FinancingProgramConfigs[financingProgramId]

  // Open modal when unread messages exist; auto-dismiss once they're read.
  useEffect(() => {
    if (hasUnread && !isLoadingCreditApp && !user?.isBannerUser) {
      setShowUnreadMessagesModal(true)
    } else if (!hasUnread) {
      setShowUnreadMessagesModal(false)
    }
  }, [hasUnread, isLoadingCreditApp, user?.isBannerUser])

  useEffect(() => {
    if (creditApplication) {
      const should =
        (isServerAnalysingCredit(creditApplication) || isServerCreatingContract(creditApplication)) &&
        canPollCreditApp(creditApplication)
      setShouldPoll(should)
    }
  }, [creditApplication, isLoadingCreditApp])

  useEffect(() => {
    const worksheet = creditApplication?.worksheet
    if (!creditApplication || !worksheet?.isHighEstimate || !worksheet?.firstPaymentOn) {
      return
    }

    const firstPaymentOnDate = new Date(worksheet.firstPaymentOn)
    const maxDateToSendInvoice = format(addDays(firstPaymentOnDate, -5), 'yyyy-MM-dd')
    const today = format(new Date(), 'yyyy-MM-dd')

    const hasClinicInvoice = creditApplication.requiredDocuments.some(
      (doc) => doc.typeId === EDocumentType.ClinicInvoice,
    )

    if (hasClinicInvoice && worksheet.isHighEstimate && today > maxDateToSendInvoice) {
      setError(new Error(t('common.errors.highEstimateUploadInvoiceDelayPassed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
  }, [creditApplication, t])
  const isPrequalComplete =
    !isLoadingCreditApp &&
    (creditApplication?.prequalificationDecision?.decision === EDecision.Approved ||
      creditApplication?.prequalificationDecision?.decision === EDecision.Refused)

  const requiredDocuments = creditApplication?.requiredDocuments.filter(
    (doc) =>
      doc.requiredFrom !== ERequiredFrom.CreditAgent &&
      !(
        doc.typeId === EDocumentType.SixMonthsPersonalBankStatements &&
        currentProgramConfig.shouldDisplayBankStatementDocument === false
      ),
  )

  return (
    <>
      <PageSpinner isLoading={isLoadingMerchant || isLoadingCreditApp} withBackdrop />
      {error && (
        <Alert
          severity="error"
          sx={{
            width: '100%',
            marginBottom: '2rem',
          }}
        >
          <AlertTitle>{t('common.error')}</AlertTitle>
          {error.message}
        </Alert>
      )}
      {creditApplication && merchant && (
        <>
          <UnreadMessagesModal
            open={showUnreadMessagesModal}
            creditApp={creditApplication}
            financingProgramId={financingProgramId}
            onClose={() => setShowUnreadMessagesModal(false)}
          />
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.spacing(2),
              mb: 2,
              justifyContent: 'space-between',
              flexDirection: 'row',
              [theme.breakpoints.down('md')]: {
                flexDirection: 'column',
              },
            })}
          >
            <Grid container spacing={2} sx={{ wordWrap: 'break-word', mt: 0 }}>
              {creditApplication && merchant?.name && (
                <Grid
                  sx={(theme) => ({
                    flex: '1 1 100%',
                    maxWidth: '100%',
                    [theme.breakpoints.up('md')]: {
                      flex: '1 1 35%',
                      maxWidth: '35%',
                    },
                  })}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <LoanInfoBox
                    creditApp={creditApplication}
                    merchant={merchant}
                    canEdit={canEdit}
                    financingProgramId={financingProgramId}
                    hasUnread={hasUnread}
                  />
                </Grid>
              )}
              {creditApplication.applicant && (
                <Grid
                  sx={(theme) => ({
                    flex: '1 1 100%',
                    maxWidth: '100%',
                    [theme.breakpoints.up('md')]: {
                      flex: '1 1 30%',
                      maxWidth: '30%',
                    },
                  })}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <ApplicantInfoBox
                    financingProgramId={financingProgramId}
                    applicant={creditApplication.applicant}
                    applicantTitle={t('viewCreditApplication.applicant')}
                    applicantType={EApplicantType.Applicant}
                    creditApplication={creditApplication}
                    disabled={
                      creditApplication?.contract?.status === EContractStatus.Completed ||
                      !canEdit ||
                      isPrequalComplete ||
                      shouldPoll
                    }
                  />
                </Grid>
              )}
              {creditApplication.coApplicant && (
                <Grid
                  sx={(theme) => ({
                    flex: '1 1 100%',
                    maxWidth: '100%',
                    [theme.breakpoints.up('md')]: {
                      flex: '1 1 30%',
                      maxWidth: '30%',
                    },
                  })}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <ApplicantInfoBox
                    financingProgramId={financingProgramId}
                    applicant={creditApplication?.coApplicant}
                    applicantTitle={t('viewCreditApplication.coApplicant')}
                    applicantType={EApplicantType.CoApplicant}
                    creditApplication={creditApplication}
                    disabled={
                      creditApplication?.contract?.status === EContractStatus.Completed ||
                      !canEdit ||
                      shouldPoll ||
                      isPrequalComplete
                    }
                  />
                </Grid>
              )}
              {!creditApplication.coApplicant &&
                creditApplication?.contract?.cvtNumber === null &&
                canEdit &&
                !shouldPoll &&
                creditApplication.originSystemId === EOriginSystemId.MerchantDashboard &&
                currentProgramConfig.supportsCoApplicant && (
                  <Grid
                    sx={(theme) => ({
                      flex: '1 1 100%',
                      maxWidth: '100%',
                      [theme.breakpoints.up('md')]: {
                        flex: '1 1 30%',
                        maxWidth: '30%',
                      },
                    })}
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Paper
                      component={Grid}
                      size="grow"
                      sx={{
                        borderRadius: 2,
                        p: 1,
                        m: 1,
                        height: '100%',
                      }}
                    >
                      <Stack direction="column" justifyContent="center" alignItems="center" height="100%" spacing={1}>
                        <IconButton
                          onClick={() => {
                            navigate(
                              `/credits/${creditApplication.financingProgramId}/${id}/edit?showTabIndex=1&addCoApp=true`,
                            )
                          }}
                        >
                          <AddIcon style={{ fontSize: '3rem' }} />
                        </IconButton>
                        {t('viewCreditApplication.addCoapp')}
                      </Stack>
                    </Paper>
                  </Grid>
                )}
            </Grid>
            <Stack spacing={2} sx={{ width: '100%' }}>
              {merchant.supportedFinancingPrograms.length > 1 && (
                <Grid
                  sx={(theme) => ({
                    flex: '1 1 100%',
                    maxWidth: '100%',
                    [theme.breakpoints.up('md')]: {
                      flex: '1 1 25%',
                      maxWidth: '25%',
                    },
                  })}
                >
                  <Chip
                    label={`${t('applicationHeader.program')} ${t(`EFinancingProgram.${creditApplication.financingProgramId}`, { ns: 'enum' })}`}
                    variant="outlined"
                  />
                </Grid>
              )}
              <Grid
                sx={(theme) => ({
                  flex: '1 1 100%',
                  maxWidth: '100%',
                  [theme.breakpoints.up('md')]: {
                    flex: '1 1 25%',
                    maxWidth: '25%',
                  },
                })}
              >
                <MerchantReferenceCodeField creditApp={creditApplication} />
              </Grid>
            </Stack>
          </Box>
          <Grid size={12}>
            <Stack spacing={2}>
              <Typography variant="h5" component="div">
                {t('viewCreditApplication.applicationProcessTracking')}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{
                  scrollbarWidth: 'thin',
                  overflowY: 'auto',
                  pb: 2,
                }}
              >
                {FinancingProgramConfigs[financingProgramId].displayPrequalify && (
                  <PrequalTile creditApp={creditApplication} merchant={merchant} />
                )}
                <CreditTile
                  creditApp={creditApplication}
                  merchant={merchant}
                  canEdit={canEdit && creditApplication.originSystemId == EOriginSystemId.MerchantDashboard}
                />
                {FinancingProgramConfigs[financingProgramId].displayIncomeVerification && (
                  <>
                    <IncomeVerificationTile
                      financingProgramId={financingProgramId}
                      applicant={creditApplication?.applicant}
                      requiredDocuments={creditApplication?.requiredDocuments}
                      creditDecision={creditApplication?.finalCreditDecision.decision}
                      applicationStatus={creditApplication?.status}
                      creditApplicationId={creditApplication.id!}
                      merchant={merchant}
                      canEdit={canEdit && creditApplication.originSystemId == EOriginSystemId.MerchantDashboard}
                    />
                    {creditApplication?.coApplicant && (
                      <IncomeVerificationTile
                        financingProgramId={financingProgramId}
                        applicant={creditApplication?.coApplicant}
                        requiredDocuments={creditApplication?.requiredDocuments}
                        creditDecision={creditApplication?.finalCreditDecision.decision}
                        applicationStatus={creditApplication?.status}
                        creditApplicationId={creditApplication.id!}
                        merchant={merchant}
                        canEdit={canEdit && creditApplication.originSystemId == EOriginSystemId.MerchantDashboard}
                      />
                    )}
                  </>
                )}
                <ContractTile creditApp={creditApplication} isPolling={shouldPoll} canEdit={canEdit} />
                <FundingTile creditApp={creditApplication} />
              </Stack>
            </Stack>
          </Grid>
          <Grid size={12}>
            <DocumentTable
              financingProgramId={financingProgramId}
              requiredDocuments={requiredDocuments}
              creditApplicationId={id}
              lastSentOn={creditApplication.merchantDocumentUploadRequestSentOn}
              setError={setError}
              canEdit={canEdit}
              worksheet={creditApplication.worksheet}
            />
          </Grid>
        </>
      )}
      {!isLoadingMerchant && !isLoadingCreditApp && !creditApplication && <NotFound />}
    </>
  )
}

export default ViewCreditApplicationPage
